import {
  faBackward,
  faChevronDown,
  faForward,
  faMinus,
  faPause,
  faPersonRunning,
  faPlay,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { AnimationPlayerTitle } from "src/core/enums/floor-analytics-enums";
import "../../analytics.scss";

interface HeatmapPlayerComponentProps {
  playerTitle: AnimationPlayerTitle;
  maxHourRange: number;
  hourRange: number;
  isPlaying: boolean;
  playbackSpeed: number;
  showControls: boolean;
  position: { x: number; y: number };
  onHourRangeChange: (hour: number) => void;
  onPlayPause: () => void;
  onStop: () => void;
  onStepForward: () => void;
  onStepBackward: () => void;
  onSpeedChange: (speed: number) => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  onToggleVisibility: () => void;
}

const slotToTime = (slot: number): string => {
  const h = Math.floor(slot / 4);
  const m = (slot % 4) * 15;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

const HeatmapPlayerComponent: React.FC<HeatmapPlayerComponentProps> = ({
  playerTitle,
  maxHourRange,
  hourRange,
  isPlaying,
  playbackSpeed,
  showControls,
  position,
  onHourRangeChange,
  onPlayPause,
  onStop,
  onStepForward,
  onStepBackward,
  onSpeedChange,
  onPositionChange,
  onToggleVisibility,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDragStart = (e: any) => {
    e.preventDefault();

    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleDragMove = (e: any) => {
    if (isDragging) {
      onPositionChange({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("pointermove", handleDragMove);
      window.addEventListener("pointerup", handleDragEnd);
      window.addEventListener("pointercancel", handleDragEnd);
    }

    return () => {
      window.removeEventListener("pointermove", handleDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    };
  }, [isDragging]);

  if (!showControls) return null;

  return (
    <div
      className={`card position-absolute shadow-sm player-container mt-3 ${
        showControls ? "visible" : ""
      }`}
      style={{
        left: position.x,
        top: position.y,
        width: "400px",
        maxHeight: "180px",
        zIndex: 101,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
      }}
    >
      <div className="card-body">
        <div
          className="d-flex align-items-center justify-content-between mb-3 gap-5 player-drag-handle"
          onPointerDown={handleDragStart}
          title="Click and hold to drag"
        >
          <h6 className="card-title mb-0">{playerTitle}</h6>
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onToggleVisibility}
            title="Minimize"
            aria-label="Minimize"
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
        </div>

        <div
          className="d-flex align-items-center justify-content-center gap-3 mt-4"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            title="Speed Control"
            className="heatmap-player-btn border-warning player-btn-wrapper"
          >
            <FontAwesomeIcon icon={faPersonRunning} color="#ff9966" />
            <select
              className="player-hidden-select"
              value={playbackSpeed}
              onChange={(e) => onSpeedChange(Number(e.target.value))}
            >
              <option value="0.5">0.5x</option>
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="4">4x</option>
            </select>
            <span className="dropdown-icon">
              <FontAwesomeIcon icon={faChevronDown} />
            </span>
          </button>

          <button
            onClick={onStepBackward}
            disabled={isPlaying}
            title="Step Backward"
            className="heatmap-player-btn border-blue"
          >
            <FontAwesomeIcon icon={faBackward} color="blue" />
          </button>

          <button
            onClick={onPlayPause}
            title={isPlaying ? "Pause" : "Play"}
            className={`heatmap-player-btn ${
              isPlaying ? "border-warning" : "border-success"
            }`}
          >
            <FontAwesomeIcon
              icon={isPlaying ? faPause : faPlay}
              color={isPlaying ? "#ffcc00" : "green"}
            />
          </button>

          <button
            onClick={onStepForward}
            disabled={isPlaying}
            title="Step Forward"
            className="heatmap-player-btn border-blue"
          >
            <FontAwesomeIcon icon={faForward} color="blue" />
          </button>

          <button
            onClick={onStop}
            title="Stop & Reset"
            className="heatmap-player-btn border-danger"
          >
            <FontAwesomeIcon icon={faStop} color="red" />
          </button>
        </div>

        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small>00:00</small>
            <small>{slotToTime(maxHourRange)}</small>
          </div>
          <div className="progress" style={{ height: "10px" }}>
          <progress
            className="progress-bar"
            value={hourRange}
            max={maxHourRange}
          />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-1">
            <small className="text-muted">
              Current Speed: {playbackSpeed}x •{" "}
              {isPlaying ? "Playing..." : "Paused"}
            </small>
            <small className="text-muted">Time: {slotToTime(hourRange)}</small>
          </div>
        </div>
      </div>
    </div>
  );
};
export default HeatmapPlayerComponent;
